// One GPU draw call in place of ServiceMapMarkers' per-marker DOM AdvancedMarkers.
import { Color } from '@deck.gl/core'
import { IconLayer, IconLayerProps, ScatterplotLayer } from '@deck.gl/layers'
import { cyan } from '@mui/material/colors'
import { alpha, useTheme } from '@mui/material/styles'
import React, { ComponentProps, useMemo, useRef } from 'react'

import { DeckOverlay } from 'components/common/map/webgl/DeckOverlay'
import { hexToRgb } from 'components/common/map/webgl/generators/colorUtils'
import { generateRouteIcon } from 'components/common/map/webgl/generators/generateRouteIcon'
import { generateWeekDayIcon } from 'components/common/map/webgl/generators/generateWeekDayIcon'
import { ServiceMarkerCanvasProps } from 'components/configured-service/Schedule/types'
import { MARKER_TYPE } from 'components/configured-service/tabs/MapRoutingTab/components/MarkerTypeControl'
import { COMMON } from 'theme/colors'

// Both the drawn default and the canvas size the icon caches below are keyed on.
const ICON_SIZE = 30
// `dimmedIds` alpha: faint enough that focused pins carry the eye, solid enough to stay locatable.
const FULL_COLOR: Color = [255, 255, 255, 255]
const DIMMED_COLOR: Color = [255, 255, 255, 70]
const CENTER_MARKER_SIZE = 40
const OVERSAMPLE = 2
const SVG_PIN_PATH = 'M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0'

// Above the google.maps.Circle service-radius overlay and the sequence lines, both at zIndex 1.
const DECK_STYLE = { zIndex: 10 }

// deck uploads any canvas as a texture at runtime; its types only name `string | Texture`.
const asIconAtlas = (canvas: HTMLCanvasElement) => canvas as unknown as NonNullable<IconLayerProps['iconAtlas']>

// Outline precedence: active beats selected beats none.
const outlineColorFor = (isActive: boolean, isSelected: boolean | undefined, activeColor: string) => {
  if (isActive) return activeColor
  return isSelected ? cyan[400] : null
}

let centerIconCanvas: HTMLCanvasElement | null = null
let centerIconBgColor: string | null = null

function generateCenterIcon(bgColor: string) {
  if (centerIconCanvas && centerIconBgColor === bgColor) return centerIconCanvas
  const dpi = (typeof window !== 'undefined' ? window.devicePixelRatio || 2 : 2) * OVERSAMPLE
  const size = CENTER_MARKER_SIZE
  const canvas = document.createElement('canvas')
  canvas.width = Math.ceil(size * dpi)
  canvas.height = Math.ceil(size * dpi)
  const ctx = canvas.getContext('2d') as CanvasRenderingContext2D
  ctx.scale(dpi, dpi)
  const cx = size / 2
  const cy = size / 2
  const radius = size / 2 - 2
  ctx.save()
  ctx.shadowColor = alpha(COMMON.black, 0.3)
  ctx.shadowBlur = 4
  ctx.shadowOffsetY = 2
  ctx.beginPath()
  ctx.arc(cx, cy, radius, 0, Math.PI * 2)
  ctx.fillStyle = bgColor
  ctx.fill()
  ctx.restore()
  const iconOff = (size - 24) / 2
  ctx.save()
  ctx.translate(iconOff, iconOff)
  ctx.strokeStyle = COMMON.white
  ctx.lineWidth = 2
  ctx.lineCap = 'round'
  ctx.lineJoin = 'round'
  ctx.stroke(new Path2D(SVG_PIN_PATH))
  ctx.beginPath()
  ctx.arc(12, 10, 3, 0, Math.PI * 2)
  ctx.stroke()
  ctx.restore()
  centerIconCanvas = canvas
  centerIconBgColor = bgColor
  return canvas
}

interface IconEntry {
  canvas: HTMLCanvasElement
  width: number
  height: number
  anchorX: number
  anchorY: number
}

// Where an icon ended up in the packed atlas, in the shape deck's `iconMapping` wants.
type IconPlacement = { x: number; y: number; width: number; height: number; anchorX: number; anchorY: number; mask: boolean }

// Module-level icon caches — re-generated only when color combination changes
const routeIconCache = new Map<string, IconEntry>()
const weekDayIconCache = new Map<string, IconEntry>()

const DEFAULT_BORDER_COLOR = COMMON.white

function getRouteIconEntry(colors: string[], outlineColor: string | null, borderColor: string) {
  const key = `${colors.length ? colors.join(',') : '__unassigned__'}|${outlineColor ?? ''}|${borderColor}`
  if (!routeIconCache.has(key)) {
    const canvas = generateRouteIcon(colors, { size: ICON_SIZE, borderWidth: 2, borderColor, outlineColor })
    routeIconCache.set(key, { canvas, width: canvas.width, height: canvas.height, anchorX: canvas.width / 2, anchorY: canvas.height / 2 })
  }
  return { key, ic: routeIconCache.get(key) as IconEntry }
}

function getWeekDayIconEntry(activeDays: string[], outlineColor: string | null, borderColor: string) {
  const key = `${activeDays.length ? activeDays.join(',') : '__none__'}|${outlineColor ?? ''}|${borderColor}`
  if (!weekDayIconCache.has(key)) {
    const canvas = generateWeekDayIcon(activeDays, { size: ICON_SIZE, borderWidth: 2, borderColor, outlineColor })
    weekDayIconCache.set(key, {
      canvas,
      width: canvas.width,
      height: canvas.height,
      anchorX: canvas.width / 2,
      anchorY: canvas.height / 2,
    })
  }
  return { key, ic: weekDayIconCache.get(key) as IconEntry }
}

function buildAtlas(icByKey: Map<string, IconEntry>) {
  if (icByKey.size === 0) {
    const empty = document.createElement('canvas')
    empty.width = 1
    empty.height = 1
    return { iconAtlas: asIconAtlas(empty), iconMapping: {} }
  }

  const MAX_W = 4096
  let rowX = 0
  let rowY = 0
  let rowH = 0
  let atlasW = 0
  const entries = Array.from(icByKey.entries()).map(([key, ic]) => {
    if (rowX + ic.width > MAX_W && rowX > 0) {
      rowY += rowH
      rowX = 0
      rowH = 0
    }
    const placed = { key, ic, x: rowX, y: rowY }
    rowX += ic.width
    if (ic.height > rowH) rowH = ic.height
    if (rowX > atlasW) atlasW = rowX
    return placed
  })
  const atlasH = rowY + rowH

  const atlas = document.createElement('canvas')
  atlas.width = atlasW
  atlas.height = atlasH
  const ctx = atlas.getContext('2d') as CanvasRenderingContext2D
  const iconMapping: Record<string, IconPlacement> = {}
  entries.forEach(({ key, ic, x, y }) => {
    ctx.drawImage(ic.canvas, x, y)
    iconMapping[key] = { x, y, width: ic.width, height: ic.height, anchorX: ic.anchorX, anchorY: ic.anchorY, mask: false }
  })
  return { iconAtlas: asIconAtlas(atlas), iconMapping }
}

const WebGLServiceMarkerLayer = ({
  markers,
  mapMarkerType,
  activeMarker,
  selectedDaysMap,
  selectedRoutesMap,
  onMouseEnter,
  onMouseLeave,
  onClick,
  radiusCircle,
  centerMarker,
  selectedIds,
  dimmedIds,
  iconSize = ICON_SIZE,
}: ServiceMarkerCanvasProps) => {
  const theme = useTheme()
  const isRouteMode = mapMarkerType === MARKER_TYPE.ROUTE
  // onHover fires on every mouse-move; without this the popover's open timer resets on each one.
  const lastHoveredIdRef = useRef<string | null>(null)

  const { iconItems, iconAtlas, iconMapping } = useMemo(() => {
    const icByKey = new Map<string, IconEntry>()
    const items = (markers || [])
      .filter(item => {
        if (isRouteMode) return true
        const { weekDays = [] } = item
        if (weekDays.length === 0) return true
        return weekDays.some(day => selectedDaysMap?.[day])
      })
      .map(item => {
        const { latitude, longitude, routes = [], weekDays = [], borderColor = DEFAULT_BORDER_COLOR } = item
        const isActive = activeMarker?.details?.id === item.id
        // Its own halo, so a lasso selection reads apart from the marker under the cursor.
        const outlineColor = outlineColorFor(isActive, selectedIds?.has(item.id), theme.palette.primary.main)
        const entry = isRouteMode
          ? getRouteIconEntry(
              (routes || [])
                .filter(route => !route.routeId || selectedRoutesMap?.[route.routeId] !== false)
                .map(route => route.color)
                .filter((color): color is string => Boolean(color)),
              outlineColor,
              borderColor
            )
          : getWeekDayIconEntry(
              weekDays.filter(day => selectedDaysMap?.[day]),
              outlineColor,
              borderColor
            )
        icByKey.set(entry.key, entry.ic)
        return {
          position: [longitude, latitude],
          iconKey: entry.key,
          details: item,
          isActive,
        }
      })
    const atlas = buildAtlas(icByKey)
    return { iconItems: items, ...atlas }
  }, [markers, isRouteMode, selectedDaysMap, selectedRoutesMap, activeMarker?.details?.id, theme.palette.primary.main, selectedIds])

  const layer = useMemo(
    () =>
      new IconLayer({
        id: 'cs-service-markers',
        data: iconItems,
        getPosition: item => item.position,
        iconAtlas,
        iconMapping,
        getIcon: item => item.iconKey,
        getColor: item => (dimmedIds?.has(item.details.id) ? DIMMED_COLOR : FULL_COLOR),
        getSize: item => (item.isActive ? iconSize * 1.3 : iconSize),
        sizeScale: 1,
        sizeUnits: 'pixels',
        sizeMinPixels: 16,
        pickable: true,
        onClick: ({ object }) => {
          if (!object || !onClick) return
          const latLng = { lat: object.details.latitude, lng: object.details.longitude }
          onClick(latLng, null, object.details)
        },
        onHover: ({ object }) => {
          const newId = object?.details?.id ?? null
          if (newId === lastHoveredIdRef.current) return
          lastHoveredIdRef.current = newId
          if (object) {
            if (onMouseEnter) {
              const latLng = { lat: object.details.latitude, lng: object.details.longitude }
              onMouseEnter(latLng, null, object.details)
            }
          } else if (onMouseLeave) {
            onMouseLeave()
          }
        },
        updateTriggers: { getSize: [activeMarker?.details?.id, iconSize], getColor: [dimmedIds] },
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [iconItems, iconAtlas, iconMapping, activeMarker?.details?.id, dimmedIds, iconSize, onClick, onMouseEnter, onMouseLeave]
  )

  // Not memoized: a finalized deck.gl layer can't be reused, and a cached one asserts on re-init.
  const radiusLayer = (() => {
    const circle = radiusCircle
    if (!circle || !circle.center || !circle.radius || circle.radius <= 0) return null
    const [fillRed, fillGreen, fillBlue] = hexToRgb(circle.fillColor)
    const [strokeRed, strokeGreen, strokeBlue] = hexToRgb(circle.strokeColor)
    const fillRgba: Color = [fillRed, fillGreen, fillBlue, Math.round((circle.fillOpacity ?? 0.3) * 255)]
    const strokeRgba: Color = [strokeRed, strokeGreen, strokeBlue, Math.round((circle.strokeOpacity ?? 1) * 255)]
    return new ScatterplotLayer({
      id: 'cs-service-radius',
      data: [{ position: [circle.center.lng, circle.center.lat] }],
      getPosition: point => point.position,
      getRadius: circle.radius,
      radiusUnits: 'meters',
      getFillColor: fillRgba,
      getLineColor: strokeRgba,
      lineWidthMinPixels: circle.strokeWeight ?? 3,
      stroked: true,
      filled: true,
      pickable: false,
    })
  })()

  // --- Center marker layer ---
  const centerLayer = (() => {
    const marker = centerMarker
    if (!marker?.position || !Number.isFinite(marker.position.lat) || !Number.isFinite(marker.position.lng)) return null
    const canvas = generateCenterIcon(marker.bgColor)
    return new IconLayer({
      id: 'cs-center-marker',
      data: [{ position: [marker.position.lng, marker.position.lat] }],
      getPosition: point => point.position,
      iconAtlas: asIconAtlas(canvas),
      iconMapping: {
        center: {
          x: 0,
          y: 0,
          width: canvas.width,
          height: canvas.height,
          anchorX: canvas.width / 2,
          anchorY: canvas.height / 2,
          mask: false,
        },
      },
      getIcon: () => 'center',
      getSize: CENTER_MARKER_SIZE,
      sizeScale: 1,
      sizeUnits: 'pixels',
      pickable: false,
    })
  })()

  const allLayers = [radiusLayer, layer, centerLayer].filter(Boolean) as ComponentProps<typeof DeckOverlay>['layers']

  return <DeckOverlay layers={allLayers} overlayInstanceRef={undefined} pane={undefined} style={DECK_STYLE} />
}

export default WebGLServiceMarkerLayer
